import { toastVariant } from '__components/ToastProvider';
import { useToast } from '__components/ToastProvider';
import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useRecordDetailCustomScript } from 'ts-components/ScriptRunner/hooks/useRecordDetailCustomScript';
import { PluginContext } from './PluginContext';
import { useSelector } from 'react-redux';

const getRouteMatcher = (objectId: string, clientObjectId: string) => {
  if (objectId === clientObjectId || objectId === 'client') {
    return { isClient: true, exp: new RegExp(`^/client/([a-zA-Z0-9-]+)/.*$`) };
  }

  return {
    isClient: false,
    exp: new RegExp(`^/custom-objects/${objectId}/([a-zA-Z0-9-]+).*$`),
  };
};

const getPluginStateKey = (item: any) => {
  return `${item.plugin_api_name}-${item.api_name}`;
};

const Runner = () => {
  const [showToast] = useToast();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const previousPathname = useRef('');
  const business = useSelector((s: any) => s.authentication.chosenBusiness);
  const clientObjectId = business?.client_object?.id;

  const { routeScripts, setRouteScriptRunState } = useContext(PluginContext);

  const [executeScript] = useRecordDetailCustomScript({
    onError: (e) => {
      showToast({
        message: `${t('Action could not run')}: ${e?.message}`,
        variant: toastVariant.FAILURE,
      });
    },
    // Only route script handlers need to be able to release a blocking script, since they're the only type that
    // can block the app rendering.
    onReleaseBlockingScript: (executionPlugin) => {
      if (executionPlugin) {
        const key = getPluginStateKey(executionPlugin);
        setRouteScriptRunState((state) => {
          return {
            ...state,
            [key]: {
              ...state[key],
              waitingOnRouteScript: false,
            },
          };
        });
      }
    },
  });

  const getRunnerStateForScript = (
    checkPath: string,
    checkObjectId: string
  ) => {
    const { exp, isClient } = getRouteMatcher(checkObjectId, clientObjectId);

    const match = exp.exec(checkPath);

    if (match?.[1]) {
      return {
        canRunScript: true,
        entityId: match[1],
        customObjectId: isClient ? clientObjectId : checkObjectId,
      };
    }

    return {
      canRunScript: false,
    };
  };

  const handlePathChange = (
    previousPath: string = '',
    currentPath: string = ''
  ) => {
    const runQueue = routeScripts
      .map((script) => {
        return {
          ...getRunnerStateForScript(currentPath, script.custom_object.id),
          ...script,
        };
      })
      .filter((q) => {
        if (!q.canRunScript) {
          return false;
        }

        if (q.routes?.length) {
          return q.routes.some((pattern) => {
            return new RegExp(pattern).test(currentPath);
          });
        }

        return true;
      });

    runQueue.forEach((item) => {
      const itemKey = getPluginStateKey(item);

      setRouteScriptRunState((state) => {
        return {
          ...state,
          [itemKey]: {
            ...state[itemKey],
            waitingOnRouteScript: item.blocking,
          },
        };
      });
      (executeScript as any)(
        item.script,
        {
          previousRoute: previousPath,
          currentRoute: currentPath,
        },
        item,
        {
          entityId: item.entityId,
          objectId: item.customObjectId,
        }
      ).then(() => {
        setRouteScriptRunState((state) => {
          return {
            ...state,
            [itemKey]: {
              ...state[itemKey],
              waitingOnRouteScript: false,
            },
          };
        });
      });
    });
  };

  if (previousPathname.current !== pathname && clientObjectId) {
    const previousValue = previousPathname.current;
    previousPathname.current = pathname;

    handlePathChange(previousValue, pathname);
  }

  return null;
};

export const RouteScriptHandler = () => {
  const { routeScripts } = useContext(PluginContext);

  if (!routeScripts.length) {
    return null;
  }

  return <Runner />;
};
